import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { formsApi } from '@/services/forms.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Creates a form.
 *
 * Only a title is required — fields are added afterward on the detail page,
 * where the draft/published split that governs editing them is visible, not
 * hidden inside a creation dialog.
 */
export function FormCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [form, setForm] = useState({ title: '', description: '' });

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      formsApi.create({
        title: form.title.trim(),
        description: form.description.trim() || null,
        fields: [],
      }),
    onSuccess: onCreated,
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors;
        setFieldErrors(fields);
        if (Object.keys(fields).length === 0) {
          setFormError(t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') }));
        }
        return;
      }
      setFormError(t('errors.INTERNAL_ERROR'));
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);
    mutation.mutate();
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog"
      aria-labelledby="form-create-title"
    >
      <form onSubmit={handleSubmit} noValidate className="dialog-body">
        <h2 id="form-create-title">{t('forms.form.create')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <Field
          label={t('forms.form.formTitle')}
          name="title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          error={fieldErrors.title}
          required
          autoFocus
        />

        <Field
          label={t('forms.form.description')}
          name="description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          error={fieldErrors.description}
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {t('common.actions.save')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
